import { List } from "immutable";
import AppConstants from "@/constants/AppConstants";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE, SYNCED_PROPS } from "@/constants/DesignConstants.mjs";
import { getProp } from "@/utils/Merge/MergeUtils.mjs";

/** @typedef {import("immutable").Map} ImmutableMap */

const { THEME_STYLES } = AppConstants;

/**
 * True when the entry stores the state prop, or any of its composite children, at this layout.
 * `getProp` handles sync, so a synced prop reads desktop.
 * @param {ImmutableMap} entry - One `theme.styles` form entry.
 * @param {string} layout - `desktop` or `mobile`.
 * @param {string} stateProp - State-addressed prop, e.g. `focus.control.bC`.
 * @param {Object} metaMap - Property meta map; a composite's `children` name its child props.
 * @returns {boolean}
 */
export const isStatePropCustomized = (entry, layout, stateProp, metaMap) => {
  const propNames = [stateProp, ...(metaMap[stateProp]?.children ?? [])];
  return propNames.some((propName) => getProp(entry, layout, propName) != null);
};

/**
 * Updates that delete a state prop on both layouts so it falls back to Default. A composite
 * removes its whole group, so `focus.control.shadow.style` removes `focus.control.shadow`. The
 * prop also leaves `sync`, so it takes its sync from Default again.
 * @param {Object} params
 * @param {ImmutableMap} params.entry - Form entry; its `id` names the update paths.
 * @param {string} params.stateProp
 * @param {Object} params.metaMap
 * @returns {Array<{ name: string, value: unknown }>}
 */
export const getRemoveCustomizationUpdates = ({ entry, stateProp, metaMap }) => {
  const path = metaMap[stateProp]?.children
    ? stateProp.split(".").slice(0, -1).join(".")
    : stateProp;
  const sync = entry.get(SYNCED_PROPS, List());
  const deletes = [LAYOUT_DESKTOP, LAYOUT_MOBILE].map((layout) => ({
    name: `${THEME_STYLES}.${entry.get("id")}.${layout}.${path}`,
    value: null,
  }));

  return sync.includes(stateProp)
    ? [
        ...deletes,
        {
          name: `${THEME_STYLES}.${entry.get("id")}.${SYNCED_PROPS}`,
          value: sync.filter((name) => name !== stateProp),
        },
      ]
    : deletes;
};
